import React, { useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import api from '../../api';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import { MainInfoStepValues } from 'components/MainInfoStep/MainInfoStep.component';
import { ShareableLinkMainInfo } from 'models/ShareableLink';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useCallbackAsync from 'hooks/useCallbackAsync';
import { getInitialPhone, Phone } from 'phoneNumberUtils';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import EmailField from 'components/EmailField';

import './ShareableLinkHasExpired.styles.scss';

export type LinkExpiredFormValues = MainInfoStepValues & {
  phone?: Phone;
};

type ShareableLinkHasExpiredProps = {
  className?: string;
  shareableLinkId: string;
  mainInfo: ShareableLinkMainInfo;
};

const ShareableLinkHasExpired: React.FC<ShareableLinkHasExpiredProps> = (
  props,
) => {
  const { className, shareableLinkId, mainInfo } = props;

  const [isRequestSent, setIsRequestSent] = useState(false);

  const classes = classNames('shareable-link-has-expired', className);

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const { firstName, lastName, companyName, email } = mainInfo;

  const initialValues = useMemo(
    () => ({
      firstName,
      lastName,
      companyName,
      email,
      phone: getInitialPhone(),
    }),
    [companyName, email, firstName, lastName],
  );

  const commonValidations = useValidations();

  const [requestAccess] = useCallbackAsync(
    async (values: LinkExpiredFormValues) => {
      await api.shareableLinks.requestAccessForExpiredLink(
        shareableLinkId,
        values,
      );
      setIsRequestSent(true);
      popSuccess(t('weSentRequestToBroker'));
    },
  );

  return (
    <Form
      initialValues={initialValues}
      onSubmit={requestAccess}
      render={({ submitting, handleSubmit }) => (
        <form onSubmit={handleSubmit} className={classes}>
          <h2>{t('shareableLinkHasExpired')}</h2>
          <div>
            <Field
              name="firstName"
              component={InputField}
              label={t('firstName')}
              required
              validate={commonValidations.name}
            />
            <Field
              name="lastName"
              component={InputField}
              label={t('lastName')}
              required
              validate={commonValidations.name}
            />
          </div>
          <div>
            <EmailField required />
            <Field
              name="companyName"
              component={InputField}
              label={t('company')}
            />
          </div>
          <div>
            <PhoneFieldWrapper />
          </div>
          <div className="actions">
            <Button type="submit" disabled={submitting || isRequestSent}>
              {t('requestAccess')}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default ShareableLinkHasExpired;
